import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { RichTextEditor } from './RichTextEditor';
import ImageUploader from './ImageUploader';

const DataForm = ({ fields, onSubmit, title, initialData, onClose }) => {
  const [formData, setFormData] = useState({});
  const [previews, setPreviews] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    } else {
      setFormData({});
    }
  }, [initialData]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.classList.add('form-is-open');
    return () => {
      document.body.style.overflow = 'unset';
      document.body.classList.remove('form-is-open');
    };
  }, []);

  const validate = () => {
    const newErrors = {};
    fields.forEach(field => {
      if (!formData[field.accessor] || (typeof formData[field.accessor] === 'string' && formData[field.accessor].trim() === '')) {
        newErrors[field.accessor] = `${field.label} is required`;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleImageCropped = (name, file, previewUrl) => {
    setPreviews(prev => ({ ...prev, [name]: previewUrl }));
    handleChange(name, previewUrl);
    handleChange(name + '_file', file);
  };

  const removeImage = (name) => {
    setPreviews(prev => {
      const newPreviews = { ...prev };
      delete newPreviews[name];
      return newPreviews;
    });
    handleChange(name, '');
    handleChange(name + '_file', null);
  };

  return createPortal(
    <div className="form-overlay" style={{ overflow: 'hidden' }} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="form-container" style={{ maxHeight: 'calc(100vh - 80px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div className="form-header" style={{ flexShrink: 0 }}>
          <h3 style={{ fontSize: '18px', fontWeight: '600' }}>{title || "Add New Entry"}</h3>
          <button type="button" className="close-form-btn" onClick={onClose}>
            <X size={18} />
          </button>
        </div>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
        <div className="data-form" style={{ overflowY: 'auto', flex: 1, paddingRight: '8px', paddingBottom: '16px' }}>
          {fields.map((field, index) => (
            <div className="form-group" key={index} style={{ gridColumn: field.fullWidth ? 'span 2' : 'span 1' }}>
              <label>{field.label} <span style={{ color: '#FF4D4D' }}>*</span></label>
              
              {field.type === 'editor' ? (
                <RichTextEditor 
                  placeholder={field.placeholder} 
                  value={formData[field.accessor] || ''}
                  onChange={(val) => handleChange(field.accessor, val)}
                  error={!!errors[field.accessor]}
                />
              ) : field.type === 'textarea' ? (
                <textarea 
                  className={errors[field.accessor] ? 'error-border' : ''}
                  placeholder={field.placeholder} 
                  rows="4" 
                  value={formData[field.accessor] || ''}
                  onChange={(e) => handleChange(field.accessor, e.target.value)}
                />
              ) : field.type === 'select' ? (
                <select 
                  className={errors[field.accessor] ? 'error-border' : ''}
                  value={formData[field.accessor] || ''}
                  onChange={(e) => handleChange(field.accessor, e.target.value)}
                >
                  <option value="">Select option</option>
                  {field.options.map((opt, i) => (
                    <option key={i} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              ) : field.type === 'file' ? (
                  <ImageUploader 
                    initialImage={previews[field.accessor] || formData[field.accessor]}
                    onImageCropped={(file, previewUrl) => handleImageCropped(field.accessor, file, previewUrl)}
                    onImageRemoved={() => removeImage(field.accessor)}
                    aspectRatio={field.aspectRatio || 1}
                    circularCrop={field.circularCrop || false}
                  />
              ) : (
                <input 
                  className={errors[field.accessor] ? 'error-border' : ''}
                  type={field.type || 'text'} 
                  placeholder={field.placeholder} 
                  value={formData[field.accessor] || ''}
                  onChange={(e) => handleChange(field.accessor, e.target.value)}
                />
              )}
              {errors[field.accessor] && (
                <span style={{ color: '#FF4D4D', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                  {errors[field.accessor]}
                </span>
              )}
            </div>
          ))}
        </div>
        <div className="form-actions" style={{ display: 'flex', gap: '12px', marginTop: '16px', borderTop: '1px solid var(--border-color)', paddingTop: '16px', flexShrink: 0 }}>
          <button 
            type="button" 
            className="cancel-btn" 
            onClick={onClose} 
            style={{
              flex: 1,
              padding: '14px',
              borderRadius: '10px',
              border: '1px solid var(--border-color)',
              background: '#F8F9FB',
              color: 'var(--text-secondary)',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="submit-btn" 
            style={{ 
              flex: 1, 
              marginTop: 0 
            }}
          >
            {initialData ? 'Update Data' : 'Submit Data'}
          </button>
        </div>
      </form>
      </div>
    </div>,
    document.body
  );
};

export { DataForm };
